import { useCallback } from 'react'
import { useWeb3React } from '@web3-react/core'
import { ConnectorNames } from 'toonswap-uikit'
import { useToast } from 'state/hooks'
import {
  Connection,
  metamaskConnection,
  walletConnectConnection,
  okxWalletConnection,
  starKeyConnection,
  trustWalletConnection,
} from 'connectors'
import { WalletConnect } from 'connectors/WalletConnect'
import { NoOkxWalletError } from 'connectors/OkxWallet'
import { NoStarKeyError, isStarKeyInjected } from 'connectors/StarKey'
import { SUPRA_MAINNET_CHAIN_ID } from 'config/constants/chains'
import { NoMetaMaskError } from 'connectors/Metamask'
import { NoTrustWalletError } from 'connectors/TrustWallet'

const useAuth = () => {
  const { chainId, connector } = useWeb3React()
  const { toastError, toastInfo } = useToast()

  const tryActivation = async (connection: Connection) => {
    if (chainId && (connection.name === 'WALLET_CONNECT' || connection.name === 'ZENGO_CONNECT')) {
      ;(connection.connector as WalletConnect).setRequiredChains([chainId])
    }

    try {
      if (connector.deactivate) {
        await connector.deactivate()
      }
      await connector.resetState()
      const isStarKey = connection === starKeyConnection
      await connection.connector.activate(SUPRA_MAINNET_CHAIN_ID)
    } catch (e) {
      console.log('error', e)
      if (e instanceof NoOkxWalletError) {
        toastInfo('Provider Error', 'OkxWallet not installed', {
          text: 'Install OKX Wallet',
          url: 'https://www.okx.com/web3',
        })
      } else if (e instanceof NoStarKeyError) {
        if (typeof window !== 'undefined' && !(window as any).starkey) {
          window.open('https://starkey.app/', '_blank')
        }
        toastInfo('Provider Error', 'StarKey not installed', {
          text: 'Install StarKey Wallet',
          url: 'https://starkey.app',
        })
      } else if (e instanceof NoMetaMaskError) {
        if (typeof window !== 'undefined') {
          window.open('https://metamask.io/download', '_blank')
        }
        toastInfo('Provider Error', 'Metamask not installed', {
          text: 'Install Metamask Wallet',
          url: 'https://metamask.io/download',
        })
      } else if (e instanceof NoTrustWalletError) {
        if (typeof window !== 'undefined') {
          window.open('https://trustwallet.com/download', '_blank')
        }
        toastInfo('Provider Error', 'Trust Wallet not installed', {
          text: 'Install Trust Wallet',
          url: 'https://trustwallet.com/download',
        })
      } else if ((e as { code?: number })?.code === 4001) {
        toastError('Connection Rejected', 'You rejected the connection request.')
      } else if (e instanceof Error && e.message.includes('remove the old')) {
        toastError('Network Conflict', e.message)
      } else {
        toastError('Provider Error', 'No provider was found')
      }
    }
  }

  const login = useCallback(async (connectorID: ConnectorNames) => {
    if (connectorID === ConnectorNames.Injected) {
      tryActivation(metamaskConnection)
    } else if (connectorID === ConnectorNames.WalletConnect) {
      tryActivation(walletConnectConnection)
    } else if (connectorID === ConnectorNames.OkxWallet) {
      tryActivation(okxWalletConnection)
    } else if (connectorID === ConnectorNames.Starkey) {
      if (!isStarKeyInjected()) {
        window.open('https://starkey.app/', '_blank')
        toastInfo('StarKey not installed', 'Install StarKey to connect', {
          text: 'Install StarKey Wallet',
          url: 'https://starkey.app',
        })
        return
      }
      tryActivation(starKeyConnection)
    } else if (connectorID === ConnectorNames.TrustWallet) {
      tryActivation(trustWalletConnection)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logout = useCallback(async () => {
    if (connector.deactivate) {
      await connector.deactivate()
    }
    if (connector) {
      await connector.resetState()
    }
  }, [connector])

  return { login, logout }
}

export default useAuth
